import { useState, useEffect, useCallback } from 'react';
import { useBudgetDataCache } from '../contexts/BudgetDataCacheContext';
import { getBudgetById, getBudgetItems } from '../api/budget';
import { BudgetItem } from '../api/budget';
import { ResourceType, getPermissionsBatchByResourceIDs } from '../api/userPermissions';
import { useUserPermissions } from '../contexts/UserPermissionsContext';

/**
 * LocalBudgetItem 类型（与 BudgetDataCacheContext 中的一致）
 */
interface LocalBudgetItem {
  budgetItemID: string;
  budgetItemCode: string;
  budgetItemName: string;
  budgetItemBelongBudgetID: string;
  budgetItemParentID: string | null;
  budgetItemAmount: number;
  budgetItemUsedAmount: number;
  budgetItemRemainingAmount: number;
  budgetItemProgressType: string;
  budgetItemCurrency?: string;
  budgetItemUnit1?: string | null;
  budgetItemUnit2?: string | null;
  budgetItemUnitPrice?: number;
  budgetItemQuantity1?: number;
  budgetItemQuantity2?: number;
  budgetItemRelatedID: string | null;
  budgetItemAssigneeID: string | null;
  budgetItemPartners: string[];
  budgetItemRemark: string;
  budgetItemFilmCode: string | null;
  budgetItemCreateTime: string;
  budgetItemUpdateTime: string;
  budgetItemLevel: number;
  budgetItemRole?: string;
  budgetItemOwnerID?: string;
  budgetItemCreatorID?: string;
  budgetItemUnusedAmount?: number;
  budgetItemExceedingAmount?: number;
  budgetItemStatus?: string;
  budgetItemDataStatus?: string;
  budgetItemChildren?: LocalBudgetItem[];
  budgetItemExpanded?: boolean;
  budgetItemChecked?: boolean;
  isRelatedItem?: boolean;
  isRelatedContainer?: boolean;
  relatedParentID?: string;
  calculatedRelatedAmount?: number;
}

/**
 * Budget 页面数据加载 Hook 配置选项
 */
export interface UseBudgetDataLoaderOptions {
  budgetId: string;
  forceRefresh?: boolean; // 是否强制从 API 刷新数据
}

/**
 * Budget 页面数据加载 Hook
 * 
 * 功能：
 * - 加载 Budget 页面所需的所有数据
 * - 智能缓存策略：首次从 API 加载，后续优先使用缓存
 * - 提供强制刷新选项
 * 
 * 加载策略：
 * 1. 检查缓存是否存在且有效
 * 2. 如果缓存有效且 forceRefresh=false，直接返回缓存数据
 * 3. 否则从 API 加载数据
 * 4. 更新缓存
 * 5. 通知订阅者
 */
export function useBudgetDataLoader(options: UseBudgetDataLoaderOptions) {
  const { budgetId, forceRefresh = false } = options;

  const { getCache, setCache } = useBudgetDataCache();
  const { setPermissions } = useUserPermissions();

  const [budgetName, setBudgetName] = useState<string>('未命名');
  const [budgetID, setBudgetID] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [budgetItems, setBudgetItems] = useState<LocalBudgetItem[]>([]);
  const [budgetStatus, setBudgetStatus] = useState<string>('预算中');
  const [budgetMainCurrency, setBudgetMainCurrency] = useState<string>('RMB');
  const [budgetOwnerID, setBudgetOwnerID] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * 构建树形结构
   */
  const buildTree = useCallback((items: BudgetItem[]): LocalBudgetItem[] => {
    const itemMap = new Map<string, LocalBudgetItem>();
    const rootItems: LocalBudgetItem[] = [];

    // 首先将所有项目转换为 LocalBudgetItem 并存储在 Map 中
    items.forEach(item => {
      const amount = Number(item.budgetItemAmount || 0);
      const usedAmount = Number(item.budgetItemUsedAmount || 0);
      
      const localItem: LocalBudgetItem = {
        ...item,
        budgetItemAmount: amount,
        budgetItemUsedAmount: usedAmount,
        budgetItemRemainingAmount: amount - usedAmount,
        budgetItemCurrency: item.budgetItemCurrency || 'RMB',
        budgetItemUnit1: item.budgetItemUnit1,
        budgetItemUnit2: item.budgetItemUnit2,
        budgetItemUnitPrice: item.budgetItemUnitPrice,
        budgetItemQuantity1: item.budgetItemQuantity1,
        budgetItemQuantity2: item.budgetItemQuantity2,
        calculatedRelatedAmount: 0,
        budgetItemRelatedID: item.budgetItemRelatedID || null,
        budgetItemAssigneeID: item.budgetItemAssigneeID || null,
        budgetItemPartners: [],
        budgetItemRemark: item.budgetItemRemark || '',
        budgetItemCreateTime: item.budgetItemCreateTime || new Date().toISOString(),
        budgetItemUpdateTime: item.budgetItemUpdateTime || new Date().toISOString(),
        budgetItemChildren: [],
        budgetItemExpanded: true,
        budgetItemChecked: false,
        budgetItemLevel: item.budgetItemLevel || 0
      };
      
      itemMap.set(item.budgetItemID, localItem);
    });

    // 然后构建树形结构
    items.forEach(item => {
      const currentItem = itemMap.get(item.budgetItemID);
      if (currentItem) {
        if (item.budgetItemParentID) {
          const parentItem = itemMap.get(item.budgetItemParentID);
          if (parentItem) {
            if (!parentItem.budgetItemChildren) {
              parentItem.budgetItemChildren = [];
            }
            parentItem.budgetItemChildren.push(currentItem);
          }
        } else {
          rootItems.push(currentItem);
        }
      }
    });

    // 递归排序函数
    const sortItems = (items: LocalBudgetItem[]): LocalBudgetItem[] => {
      return items.sort((a, b) => {
        const aCode = a.budgetItemCode || '';
        const bCode = b.budgetItemCode || '';
        return aCode.localeCompare(bCode);
      });
    };

    // 先排序
    rootItems.forEach(item => {
      if (item.budgetItemChildren) {
        item.budgetItemChildren = sortItems(item.budgetItemChildren);
      }
    });
    rootItems.sort((a, b) => (a.budgetItemCode || '').localeCompare(b.budgetItemCode || ''));

    return rootItems;
  }, []);

  /**
   * 更新父级金额
   */
  const updateParentAmounts = useCallback((items: LocalBudgetItem[]): LocalBudgetItem[] => {
    return items.map(item => {
      if (item.budgetItemChildren && item.budgetItemChildren.length > 0) {
        const children = updateParentAmounts(item.budgetItemChildren);
        const totalAmount = children.reduce((sum, child) => sum + (child.budgetItemAmount || 0), 0);
        const totalUsedAmount = children.reduce((sum, child) => sum + (child.budgetItemUsedAmount || 0), 0);
        const totalRelatedAmount = children.reduce((sum, child) => sum + (child.calculatedRelatedAmount || 0), 0);
        
        return {
          ...item,
          budgetItemChildren: children,
          budgetItemAmount: totalAmount,
          budgetItemUsedAmount: totalUsedAmount,
          budgetItemRemainingAmount: totalAmount - totalUsedAmount,
          calculatedRelatedAmount: totalRelatedAmount > 0 ? totalRelatedAmount : undefined
        };
      }
      return item;
    });
  }, []);

  /**
   * 收集所有预算项ID（包括子项）
   */
  const collectBudgetItemIDs = useCallback((items: LocalBudgetItem[]): string[] => {
    const ids: string[] = [];
    const collect = (items: LocalBudgetItem[]) => {
      items.forEach((item) => {
        ids.push(item.budgetItemID);
        if (item.budgetItemChildren && item.budgetItemChildren.length > 0) {
          collect(item.budgetItemChildren);
        }
      });
    };
    collect(items);
    return ids;
  }, []);

  /**
   * 从缓存加载数据
   */
  const loadFromCache = useCallback(() => {
    const cache = getCache(budgetId);

    if (cache) {
      setBudgetName(cache.budgetName || '未命名');
      setBudgetID(cache.budgetID);
      setTotalAmount(cache.totalAmount || 0);
      setBudgetItems(cache.budgetItems || []);
      setBudgetStatus(cache.budgetStatus || '预算中');
      setBudgetMainCurrency(cache.budgetMainCurrency || 'RMB');
      setBudgetOwnerID(cache.budgetOwnerID);
    }

    return {
      hasCache: !!cache,
      hasBudgetItems: !!(cache && cache.budgetItems && cache.budgetItems.length > 0),
    };
  }, [budgetId, getCache]);

  /**
   * 从 API 加载数据
   */
  const loadFromAPI = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 并行加载预算数据和预算项数据
      const [budgetResponse, itemsResponse] = await Promise.all([
        getBudgetById(budgetId),
        getBudgetItems(budgetId),
      ]);

      if (!budgetResponse?.data) {
        throw new Error('未找到预算数据');
      }

      if (!itemsResponse?.data) {
        throw new Error('未找到预算项数据');
      }

      const budgetData = budgetResponse.data;
      const itemsData = itemsResponse.data;

      // 构建树形结构
      const sortedItems = buildTree(itemsData);
      // 更新父级金额
      const itemsWithUpdatedAmounts = updateParentAmounts(sortedItems);

      // 更新状态
      setBudgetName(budgetData.budgetName || '未命名预算');
      setBudgetID(budgetData.budgetID);
      setTotalAmount(budgetData.budgetTotalAmount || 0);
      setBudgetItems(itemsWithUpdatedAmounts);
      setBudgetStatus(budgetData.budgetStatus || '预算中');
      setBudgetMainCurrency(budgetData.budgetCurrency || 'RMB');
      setBudgetOwnerID(budgetData.budgetOwnerID);

      // 更新缓存
      const existingCache = getCache(budgetId);
      setCache(budgetId, {
        ...existingCache,
        budgetItems: itemsWithUpdatedAmounts,
        budgetName: budgetData.budgetName || '未命名预算',
        budgetID: budgetData.budgetID,
        budgetOwnerID: budgetData.budgetOwnerID,
        totalAmount: budgetData.budgetTotalAmount || 0,
        budgetMainCurrency: budgetData.budgetCurrency || 'RMB',
        budgetStatus: budgetData.budgetStatus || existingCache?.budgetStatus || '预算中',
      });

      // 获取权限数据
      try {
        // 收集所有预算项ID
        const budgetItemIDs = collectBudgetItemIDs(itemsWithUpdatedAmounts);
        
        // 批量获取预算权限
        const budgetPermissionsMap = await getPermissionsBatchByResourceIDs(ResourceType.BUDGET, [budgetId]);
        budgetPermissionsMap.forEach((permissions, resourceID) => {
          setPermissions(ResourceType.BUDGET, resourceID, permissions);
        });

        // 批量获取预算项权限
        if (budgetItemIDs.length > 0) {
          const budgetItemPermissionsMap = await getPermissionsBatchByResourceIDs(ResourceType.BUDGET_ITEM, budgetItemIDs);
          budgetItemPermissionsMap.forEach((permissions, resourceID) => {
            setPermissions(ResourceType.BUDGET_ITEM, resourceID, permissions);
          });
        }
      } catch (permError) {
        console.warn('[useBudgetDataLoader] 获取权限数据失败（不影响主流程）:', permError);
      }

      console.log('[useBudgetDataLoader] 数据加载成功:', {
        budgetId,
        budgetName: budgetData.budgetName,
        itemsCount: itemsWithUpdatedAmounts.length,
      });
    } catch (err) {
      console.error('[useBudgetDataLoader] 数据加载失败:', err);
      setError(err instanceof Error ? err.message : '数据加载失败');
    } finally {
      setLoading(false);
    }
  }, [
    budgetId,
    buildTree,
    updateParentAmounts,
    collectBudgetItemIDs,
    getCache,
    setCache,
    setPermissions,
  ]);

  /**
   * 刷新数据（强制从 API 重新加载）
   */
  const refresh = useCallback(async () => {
    await loadFromAPI();
  }, [loadFromAPI]);

  /**
   * 初始加载数据
   */
  useEffect(() => {
    if (!budgetId || budgetId.trim() === '') {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      // 如果强制刷新，直接从 API 加载，完全跳过缓存检查
      if (forceRefresh) {
        console.log('[useBudgetDataLoader] 强制刷新，从 API 加载最新数据:', { budgetId });
        await loadFromAPI();
        return;
      }

      // 尝试从缓存加载
      const cacheStatus = loadFromCache();

      // 如果缓存完整，使用缓存数据
      if (cacheStatus.hasCache && cacheStatus.hasBudgetItems) {
        console.log('[useBudgetDataLoader] 使用缓存数据:', { budgetId });
        setLoading(false);
        return;
      }

      // 缓存不完整，从 API 加载
      console.log('[useBudgetDataLoader] 缓存不完整，从 API 加载:', {
        budgetId,
        cacheStatus,
      });
      await loadFromAPI();
    };

    loadData();
  }, [budgetId, forceRefresh, loadFromCache, loadFromAPI]);

  return {
    budgetName,
    budgetID,
    totalAmount,
    budgetItems,
    budgetStatus,
    budgetMainCurrency,
    budgetOwnerID,
    loading,
    error,
    refresh,
  };
}
